import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Package, QrCode, Minus } from 'lucide-react';

export function InventoryTab({ inventory, search, setSearch, systemFilter, setSystemFilter, showQR, onConsume }) {
  const [consumeOpen, setConsumeOpen] = React.useState(false);
  const [activeBatch, setActiveBatch] = React.useState(null);
  const [consumeQty, setConsumeQty] = React.useState('');
  const openConsume = function(item) { setActiveBatch(item); setConsumeQty(''); setConsumeOpen(true); };
  const doConsume = function() {
    const q = parseFloat(consumeQty);
    if (!q || q <= 0) return;
    Promise.resolve(onConsume(activeBatch.batch_id, q)).then(function() { setConsumeOpen(false); }).catch(function() {});
  };
  return (
    <div>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input placeholder="Search by batch or item code..." value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" />
            </div>
            <Select value={systemFilter} onValueChange={setSystemFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Systems</SelectItem>
                <SelectItem value="ykk">YKK</SelectItem>
                <SelectItem value="unitized">Unitized</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          {inventory.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
              <Package size={48} className="mb-4 opacity-50" />
              <p>No inventory items found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Batch</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">System</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Value (₹)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Taxable (₹)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">QR</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.map(function(item) {
                    var systemClass = item.system_type === 'ykk' ? 'bg-blue-100 text-blue-700' : item.system_type === 'unitized' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700';
                    var statusClass = item.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';
                    return (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-mono text-sm">
                          <div>{item.batch_number || item.batch_id}</div>
                          {item.batch_number && <div className="text-[10px] text-muted-foreground">{item.batch_id}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm">{item.item_code}</div>
                          <div className="text-xs text-muted-foreground">{item.item_name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={'px-2 py-1 rounded text-xs ' + systemClass}>
                            {(item.system_type || '').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">{item.location_id}</td>
                        <td className="px-4 py-3">{item.quantity}{item.item_type === 'profile' && item.total_length_m ? <span className="text-xs text-muted-foreground"> · {item.total_length_m}m</span> : ''}</td>
                        <td className="px-4 py-3 font-mono text-sm">{(item.value || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 font-mono text-sm">{(item.taxable_value || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          <span className={'px-2 py-1 rounded text-xs ' + statusClass}>{item.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={function() { showQR('batch', item.batch_id, item.batch_id); }}>
                            <QrCode size={14} />
                          </Button>
                        </td>
                        <td className="px-4 py-3">
                          {onConsume && item.quantity > 0 && item.status !== 'consumed' && (
                            <Button variant="outline" size="sm" onClick={function() { openConsume(item); }} data-testid={'consume-' + item.batch_id}>
                              <Minus size={14} className="mr-1" />Consume
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={consumeOpen} onOpenChange={setConsumeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Consume / Dispatch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{activeBatch?.item_name} · Available: {activeBatch?.quantity} at {activeBatch?.location_id}</p>
            <div>
              <Label>Quantity to consume</Label>
              <Input type="number" value={consumeQty} onChange={function(e) { setConsumeQty(e.target.value); }} className="mt-1.5" data-testid="consume-qty" />
            </div>
            <Button className="w-full btn-accent" onClick={doConsume} data-testid="consume-confirm-btn">Confirm Consume</Button>
            <p className="text-xs text-muted-foreground text-center">This deducts from inventory and updates the Material Library.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
